<script setup>
import { computed, ref } from 'vue'

import AppMenuItem from './AppMenuItem.vue'
import { useAppUserStore } from '@/modules/app-user/stores/app-user.store.js'
import { useRoute, useRouter } from 'vue-router'

const router = useRouter()
const route = useRoute()
const userStore = useAppUserStore()

// Get pending counts from API or store
const pendingCounts = ref({
  customers: 0,
  vouchers: 0,
  notifications: 0,
})

// Define menu structure with permissions
const menuItems = [
  {
    label: 'Home',
    items: [
      {
        label: 'Dashboard',
        icon: 'pi pi-fw pi-chart-pie',
        to: '/',
      },
    ],
  },
  {
    label: 'Reports',
    permission: 'menu.reports',
    items: [
      {
        label: 'Reports',
        icon: 'pi pi-fw pi-file',
        items: [
          {
            label: 'Customers',
            icon: 'pi pi-fw pi-list',
            to: '/report/customers',
            permission: 'report.customers',
          },
          {
            label: 'Customers Enrollment',
            icon: 'pi pi-fw pi-list',
            to: '/report/customers/enrollment',
            permission: 'report.customer_enrollment',
          },
          {
            label: 'Customers Top Purchase',
            icon: 'pi pi-fw pi-list',
            to: '/report/customers/top-purchase',
            permission: 'report.customers_top_purchase',
          },
          {
            label: 'FuelPay Tokens',
            icon: 'pi pi-fw pi-list',
            to: '/report/vouchers',
            permission: 'report.vouchers',
          },
          {
            label: 'FuelPay Redemption',
            icon: 'pi pi-fw pi-list',
            to: '/report/vouchers/redemption',
            permission: 'report.vouchers_redemption',
          },
          {
            label: 'FuelPay Redemption(Coupon)',
            icon: 'pi pi-fw pi-list',
            to: '/report/vouchers/redemption-coupon',
            permission: 'report.vouchers_redemption',
          },
          {
            label: 'FuelPay Distribution',
            icon: 'pi pi-fw pi-list',
            to: '/report/vouchers/distribution',
            permission: 'report.vouchers_distribution',
          },
          {
            label: 'Transactions',
            icon: 'pi pi-fw pi-list',
            to: '/report/transactions',
            permission: 'report.transactions',
          },
          {
            label: 'Fuel Stations Performer',
            icon: 'pi pi-fw pi-list',
            to: '/report/fuel-stations-performer',
            permission: 'report.reviews_fuel_station',
          },
          {
            label: 'Attendant Reviews',
            icon: 'pi pi-fw pi-list',
            to: '/report/staff-reviews',
            permission: 'report.reviews_attendant',
          },
          {
            label: 'Promotions',
            icon: 'pi pi-fw pi-list',
            to: '/report/content',
            permission: 'report.promotions',
          },
          {
            label: 'Mega Wallet',
            icon: 'pi pi-fw pi-list',
            to: '/report/loyalty/mega-wallet',
            permission: 'report.loyalty_mega_wallet',
          },
          {
            label: 'Mega Wallet Funding',
            icon: 'pi pi-fw pi-list',
            to: '/report/loyalty/mega-wallet/funding',
            permission: 'report.loyalty_mega_wallet_funding',
          },
          {
            label: 'Coupons',
            icon: 'pi pi-fw pi-list',
            to: '/report/loyalty/coupons',
            permission: 'report.loyalty_coupons',
          },
          {
            label: 'Wallet Transactions',
            icon: 'pi pi-fw pi-list',
            to: '/report/loyalty/wallet-transactions',
            permission: 'report.loyalty_wallet_transactions',
          },
        ],
      },
    ],
  },
  {
    label: 'Management',
    permission: 'menu.management',
    items: [
      {
        label: 'Customers',
        icon: 'pi pi-fw pi-user',
        to: '/customers',
        permission: 'customer.view',
      },
      {
        label: 'Dashboard Users',
        icon: 'pi pi-fw pi-users',
        to: '/users/admin',
        permission: 'user.view',
      },
      {
        label: 'Roles',
        icon: 'pi pi-fw pi-user-edit',
        to: '/roles',
        permission: 'role.view',
      },
      {
        label: 'Staff',
        icon: 'pi pi-fw pi-users',
        to: '/staff',
        permission: 'staff.view',
      },
      {
        label: 'Login Histories',
        icon: 'pi pi-fw pi-users',
        to: '/login-history',
        permission: 'role.view',
      },
      {
        label: 'My Login History',
        icon: 'pi pi-fw pi-users',
        to: '/login-history/me',
      },
    ],
  },
  {
    label: 'Fuel Operations',
    permission: 'menu.fuel_operation',
    items: [
      {
        label: 'Fuel Stations',
        icon: 'pi pi-fw pi-building',
        to: '/fuel-stations',
        permission: 'station.view',
      },
      {
        label: 'Fuel Types/Price',
        icon: 'pi pi-fw pi-gauge',
        to: '/fuel-types',
        permission: 'fuel_type.view',
      },
      {
        label: 'FuelPay Token',
        icon: 'pi pi-fw pi-ticket',
        to: '/vouchers',
        permission: 'voucher.view',
      },
      {
        label: 'Redemptions',
        icon: 'pi pi-fw pi-ticket',
        to: '/redemptions',
        permission: 'voucher.view',
      },
      {
        label: 'Transactions',
        icon: 'pi pi-fw pi-receipt',
        to: '/transactions',
        permission: 'transaction.view',
      },
      {
        label: 'Whitelists',
        icon: 'pi pi-fw pi-list',
        to: '/whitelists',
        permission: 'whitelist.view',
      },
    ],
  },
  {
    label: 'Loyalty & Offers',
    permission: 'menu.loyalty_offer',
    items: [
      {
        label: 'Loyalty',
        icon: 'pi pi-fw pi-wallet',
        items: [
          {
            label: 'Mega Wallet',
            icon: 'pi pi-fw pi-wallet',
            to: '/loyalty/mega-wallet',
            permission: 'mega_wallet.view',
          },
          {
            label: 'Benchmark Prices',
            icon: 'pi pi-fw pi-chart-line',
            to: '/loyalty/benchmark-prices',
            permission: 'benchmark_price.view',
          },
          {
            label: 'Badges',
            icon: 'pi pi-fw pi-star',
            to: '/loyalty/badges',
            permission: 'loyalty_badge.view_all',
          },
          {
            label: 'Wallets',
            icon: 'pi pi-fw pi-wallet',
            to: '/loyalty/wallets-statistics',
            permission: 'loyalty_wallet.view_all',
          },
          {
            label: 'Coupons',
            icon: 'pi pi-fw pi-ticket',
            to: '/loyalty/coupons-statistics',
            permission: 'loyalty_coupon.view_all',
          },
        ],
      },
      {
        label: 'Offers',
        icon: 'pi pi-fw pi-question-circle',
        to: '/offers',
        permission: 'offer.view',
      },
    ],
  },
  {
    label: 'Communication & Feedback',
    permission: 'menu.communication_feedback',
    items: [
      {
        label: 'Reviews',
        icon: 'pi pi-fw pi-star',
        permission: 'review.view',
        items: [
          // {
          //   label: 'Fuel Station Reviews',
          //   icon: 'pi pi-fw pi-list',
          //   to: '/fuel-station-reviews',
          //   permission: 'station_review.view',
          // },
          {
            label: 'Attendant Reviews',
            icon: 'pi pi-fw pi-list',
            to: '/staff-reviews',
            permission: 'attendant_review.view',
          },
        ],
      },
      {
        label: 'Infodesk',
        icon: 'pi pi-fw pi-megaphone',
        to: '/announcements',
        permission: 'announcement.view',
      },
      {
        label: 'Faqs',
        icon: 'pi pi-fw pi-question-circle',
        to: '/faqs',
        permission: 'faq.view',
      },
      {
        label: 'Security Question',
        icon: 'pi pi-fw pi-question-circle',
        to: '/questions',
        permission: 'security_question.view', // Adjust based on your actual permission
      },
      {
        label: 'Notification',
        icon: 'pi pi-fw pi-bell',
        to: '/Notifications',
        permission: 'notification.view',
      },
      {
        label: 'Survey',
        icon: 'pi pi-fw pi-star',
        to: '/survey',
        permission: 'survey.view',
      },
    ],
  },
  {
    label: 'Configuration',
    permission: 'menu.configuration',
    items: [
      {
        label: 'SMS',
        icon: 'pi pi-fw pi-inbox',
        permission: 'sms_provider.manage',
        items: [
          {
            label: 'Providers',
            icon: 'pi pi-fw pi-user',
            to: '/sms-providers',
            permission: 'sms_provider.manage',
          },
          {
            label: 'Delivery Logs',
            icon: 'pi pi-fw pi-list',
            to: '/sms-providers/delivery-logs',
            permission: 'sms_provider.manage',
          },
        ],
      },
      {
        label: 'Payment Method',
        icon: 'pi pi-fw pi-credit-card',
        to: '/payment-methods',
        permission: 'payment_method.view',
      },
      {
        label: 'Service & Amenities',
        icon: 'pi pi-fw pi-cog',
        to: '/services',
        permission: 'service.view',
      },
      {
        label: 'Tags',
        icon: 'pi pi-fw pi-tag',
        to: '/tags',
        permission: 'customer_tag.view',
      },
    ],
  },
]

// Recursive function to filter menu items based on permissions
const filterMenuByPermissions = items => {
  return items
    .map(item => {
      // Clone the item to avoid mutating the original
      const filteredItem = { ...item }

      // Check if item has permission requirement
      if (item.permission !== undefined && item.permission !== null) {
        // If user doesn't have permission, exclude this item
        if (!userStore.hasPermission(item.permission)) {
          return null
        }
      }

      // If item has nested items, filter them recursively
      if (item.items && Array.isArray(item.items)) {
        const filteredSubItems = filterMenuByPermissions(item.items)

        // If all sub-items are filtered out, hide the parent too
        if (filteredSubItems.length === 0) {
          return null
        }

        filteredItem.items = filteredSubItems
      }

      return filteredItem
    })
    .filter(item => item !== null) // Remove null items
}

// Computed property for filtered menu model
const model = computed(() => {
  return filterMenuByPermissions(menuItems)
})
</script>

<template>
  <ul class="layout-menu">
    <template v-for="(item, i) in model" :key="item">
      <app-menu-item v-if="!item.separator" :item="item" :index="i"></app-menu-item>
      <li v-if="item.separator" class="menu-separator"></li>
    </template>
  </ul>
</template>

<style lang="scss" scoped></style>
